import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Garage Estimator (Phase 13B). `garage` had no calculation
 * methodology at all before this phase - the same "honest zero-value
 * placeholder" starting point the ten Phase 13A interior rooms and
 * Phase 11's deck/patio were in.
 *
 * Deliberately a bespoke calculator (not built on
 * `createInteriorRoomCalculator`) because a garage's distinguishing
 * scope - the garage door(s), and a genuinely garage-specific electrical
 * tier (a subpanel/240V circuit for EV charging or a workshop, well
 * beyond the generic electrical-changes flag every project type asks) -
 * doesn't map onto that factory's flooring/painting/trim/lighting/
 * built-ins/windows-doors shape. The garage door in particular is priced
 * as its own quantity/tier-driven category (mirrors deck/patio's stairs
 * category) since it's routinely one of the largest single line items in
 * a garage renovation.
 *
 * Attached vs. detached (`garage_attachment`) doesn't change which
 * categories are priced, but does change what's assumed/warned about:
 * an attached garage shares a wall (and, in most Canadian jurisdictions,
 * a fire-separation requirement) with the house, while a detached
 * garage's electrical typically needs its own dedicated service run from
 * the house's panel - see "Special handling" below.
 */

const FLOORING_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  basic_coating: "garage_flooring_basic_coating",
  epoxy: "garage_flooring_epoxy"
};

const INSULATION_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  walls_only: "garage_insulation_walls",
  walls_and_ceiling: "garage_insulation_walls_ceiling"
};

const DRYWALL_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  basic: "garage_drywall_basic",
  finished_painted: "garage_drywall_finished_painted"
};

const ELECTRICAL_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  subpanel_240v: "garage_electrical_subpanel_240v",
  workshop_circuits: "garage_electrical_workshop_circuits"
};

const STORAGE_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  basic_shelving: "garage_storage_basic_shelving",
  custom_system: "garage_storage_custom_system"
};

const HEATING_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  electric_baseboard: "garage_heating_electric_baseboard",
  radiant_floor: "garage_heating_radiant_floor",
  gas_forced_air: "garage_heating_gas_forced_air"
};

const GARAGE_DOOR_ITEM_BY_TIER: Record<string, string | null> = {
  keep_existing: null,
  single_standard: "garage_door_single_standard",
  single_insulated: "garage_door_single_insulated",
  double_standard: "garage_door_double_standard",
  double_insulated: "garage_door_double_insulated"
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

export const calculateGarage: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;
  const attachment = rawString(input, "garage_attachment") ?? "attached";

  let baseTypicalForPercentage = 0;

  if (sqft && sqft > 0) {
    // --- Base renovation (anchor) + scope adjustment -------------------------
    const base = applyItem(catalog, "garage_base_renovation", { quantity: sqft, finishLevel, regionalMultiplier });
    if (base) {
      const scopeMultiplier = getScopeMultiplier(input.scope);
      const scoped = scaleAppliedItem(base, scopeMultiplier);
      applied.push(scoped);
      baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
      assumptions.push({
        code: "scope_multiplier_applied",
        message: `Base renovation cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
      });
    }

    // --- Flooring (tiered, sqft) --------------------------------------------
    const flooringTier = rawString(input, "garage_flooring");
    const flooringItemId = flooringTier ? FLOORING_ITEM_BY_TIER[flooringTier] : null;
    if (flooringItemId) {
      const flooring = applyItem(catalog, flooringItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (flooring) applied.push(flooring);
    } else {
      exclusions.push({ code: "excludes_garage_flooring", message: "No garage floor coating was requested - excluded from this estimate." });
    }

    // --- Insulation (tiered, sqft) ------------------------------------------
    const insulationTier = rawString(input, "garage_insulation");
    const insulationItemId = insulationTier ? INSULATION_ITEM_BY_TIER[insulationTier] : null;
    if (insulationItemId) {
      const insulation = applyItem(catalog, insulationItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (insulation) applied.push(insulation);
    } else {
      exclusions.push({ code: "excludes_garage_insulation", message: "No insulation upgrade was requested - excluded from this estimate." });
    }

    // --- Drywall (tiered, sqft) -----------------------------------------------
    const drywallTier = rawString(input, "garage_drywall");
    const drywallItemId = drywallTier ? DRYWALL_ITEM_BY_TIER[drywallTier] : null;
    if (drywallItemId) {
      const drywall = applyItem(catalog, drywallItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (drywall) applied.push(drywall);
    } else {
      exclusions.push({ code: "excludes_garage_drywall", message: "No drywall work was requested - excluded from this estimate." });
    }
  } else {
    anchorMissing = true;
  }

  // --- Garage door(s) (tiered, its own major line item) -----------------------
  const doorTier = rawString(input, "garage_door_scope") ?? "keep_existing";
  const doorItemId = GARAGE_DOOR_ITEM_BY_TIER[doorTier];
  if (doorItemId) {
    const door = applyItem(catalog, doorItemId, { finishLevel, regionalMultiplier });
    if (door) applied.push(door);
  } else {
    exclusions.push({ code: "excludes_garage_door", message: "The existing garage door(s) are being kept - excluded from this estimate." });
  }

  // --- Garage-specific electrical tier (beyond the generic changes flag) ------
  const electricalTier = rawString(input, "garage_electrical_upgrade");
  const electricalItemId = electricalTier ? ELECTRICAL_ITEM_BY_TIER[electricalTier] : null;
  if (electricalItemId) {
    const electrical = applyItem(catalog, electricalItemId, { finishLevel, regionalMultiplier });
    if (electrical) applied.push(electrical);
    if (attachment === "detached") {
      warnings.push({
        code: "garage_detached_electrical_service_run",
        message:
          "A detached garage's electrical upgrade typically requires a new dedicated underground or overhead service run from the house's panel, not just in-garage wiring - trenching distance and the existing panel's spare capacity can't be assessed from a questionnaire and may add materially to this cost.",
        severity: "caution"
      });
    }
  } else {
    exclusions.push({ code: "excludes_garage_electrical_upgrade", message: "No garage-specific electrical upgrade (subpanel/240V or workshop circuits) was requested - excluded from this estimate." });
  }

  // --- Storage / organization systems (tiered) ---------------------------------
  const storageTier = rawString(input, "garage_storage");
  const storageItemId = storageTier ? STORAGE_ITEM_BY_TIER[storageTier] : null;
  if (storageItemId) {
    const storage = applyItem(catalog, storageItemId, { finishLevel, regionalMultiplier });
    if (storage) applied.push(storage);
  } else {
    exclusions.push({ code: "excludes_garage_storage", message: "No storage/organization system was requested - excluded from this estimate." });
  }

  // --- Heating (tiered) ------------------------------------------------------
  const heatingTier = rawString(input, "garage_heating");
  const heatingItemId = heatingTier ? HEATING_ITEM_BY_TIER[heatingTier] : null;
  if (heatingItemId) {
    const heating = applyItem(catalog, heatingItemId, { finishLevel, regionalMultiplier });
    if (heating) applied.push(heating);
    if (heatingTier === "gas_forced_air") {
      warnings.push({
        code: "garage_gas_heating_permit_notice",
        message:
          "Adding gas-fired heating to a garage typically requires a gas fitting permit, a dedicated gas line run, and code-compliant venting/combustion air - confirm feasibility and permit requirements with a licensed gas contractor before finalizing budget.",
        severity: "caution"
      });
    }
  } else {
    exclusions.push({ code: "excludes_garage_heating", message: "No garage heating was requested - excluded from this estimate." });
  }

  // --- Plumbing (generic flag - e.g. a floor drain or hose bib) ---------------
  if (input.workType.plumbingChanges === "yes" && findItem(catalog, "garage_plumbing")) {
    const plumbing = applyItem(catalog, "garage_plumbing", { finishLevel, regionalMultiplier });
    if (plumbing) applied.push(plumbing);
  } else {
    exclusions.push({ code: "excludes_garage_plumbing", message: "No plumbing changes (e.g. a floor drain or hose bib) were requested - excluded from this estimate." });
  }

  // --- Demolition --------------------------------------------------------------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "garage_demolition")) {
    const demolition = applyItem(catalog, "garage_demolition", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility -------------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "garage_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Permit / design allowance -------------------------------------------------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "garage_permit_design", { finishLevel, regionalMultiplier });
    if (permitDesign) applied.push(permitDesign);
  }

  // --- Special handling: standing, garage-specific disclosures -----------------
  // Per the same "surface it, don't leave it implicit" discipline
  // basement's/deck-patio's special-handling warnings established -
  // neither of these can be resolved from a self-reported questionnaire.
  if (attachment === "attached") {
    warnings.push({
      code: "garage_fire_separation_code_requirement",
      message:
        "An attached garage shares structure with the house and, under most Canadian building codes, requires a fire-rated separation (wall/ceiling assembly and a self-closing fire-rated door) between the garage and living space. This estimate assumes the existing separation is code-compliant - confirm this on-site, especially if drywall/insulation is being opened up, before finalizing budget.",
      severity: "caution"
    });
  } else {
    warnings.push({
      code: "garage_detached_foundation_condition_unknown",
      message:
        "A detached garage's existing slab/foundation condition (cracking, settling, moisture) can't be assessed from a questionnaire and can materially affect flooring and insulation work - a site inspection is recommended before finalizing budget.",
      severity: "caution"
    });
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
